import { VStack } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";

import { IEReservationIntervalElement } from "modules/appointment/types/IEReservationIntervalElement";
import { useEditAppointmentNotification } from "modules/user/presentation/useEditAppointmentNotification";
import { IEReservationQueueFormField } from "modules/queues/types/IEReservationQueueFormField";
import { IAppointmentActionResponse } from "modules/appointment/types/IAppointmentActionResponse";
import { useEditAppointment } from "modules/appointment/infrastructure/useEditAppointment";
import { IEditAppointmentForm } from "modules/appointment/types/IEditAppointmentForm";
import { QueueField } from "modules/appointment/presentation/QueueField";
import { useContextConsumer } from "modules/context/Providers/ContextProvider";

import { useGetFriendlyUrl } from "utils/hooks/useGetFriendlyUrl";

import {
  CheckBoxField,
  Form,
  IErrorField,
  RecaptchaField,
  SpinnerButton,
  TextField,
} from "components/Form";
import { ChakraText } from "components/ChakraText";
import { DangerousInnerHtml } from "components/DangerousInnerHtml";

import { useEditAppointmentFormValidationSchema } from "./useEditAppointmentFormValidationSchema";

interface IProps {
  isOpen: boolean;
  appointment: IAppointmentActionResponse;
  intervalElement: IEReservationIntervalElement;
  formFields: IEReservationQueueFormField[];
  tokenId: string;
  onClose: () => void;
}

const EditAppointmentForm = ({
  appointment,
  intervalElement,
  formFields,
  tokenId,
  onClose,
}: IProps) => {
  const {
    showErrorNotification,
    showSuccessNotification,
  } = useEditAppointmentNotification();
  const { personalDataAgreement, regulationsAgreement } = useContextConsumer();
  const { friendlyUrl } = useGetFriendlyUrl();
  const history = useHistory();
  const mutation = useEditAppointment(intervalElement.id, tokenId);
  const editAppointmentFormValidationSchema = useEditAppointmentFormValidationSchema(
    formFields
  );

  const handleSubmit = async (
    model: IEditAppointmentForm,
    setCustomErrors: (errors: IErrorField[]) => void
  ) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      formData.append(
        key,
        model[key as keyof typeof model]
          ? model[key as keyof typeof model].toString()
          : ""
      );
    });
    try {
      await mutation.mutateAsync(formData);
      showSuccessNotification();
      onClose();
      history.push(`/e-rezerwacja/${friendlyUrl}`);
    } catch (e) {
      if (e.status === 400) {
        setCustomErrors(e.response.msg);
      }
      showErrorNotification(e.message);
    }
  };

  return (
    <Form<IEditAppointmentForm>
      onSubmit={handleSubmit}
      defaultValues={{
        email: appointment.email,
        name: appointment.userName,
        phone: appointment.userPhone,
        details: appointment.details,
      }}
      validationSchema={editAppointmentFormValidationSchema}
      resetOnSubmit={false}
    >
      {() => {
        return (
          <VStack spacing={4} align="stretch">
            <TextField label="Adres email" name="email" isDisabled />
            {formFields.map((field) => {
              return <QueueField field={field} />;
            })}
            <DangerousInnerHtml html={regulationsAgreement} />
            <CheckBoxField
              mt={2}
              name="termAccept"
              label={<DangerousInnerHtml html={personalDataAgreement} />}
            />
            <RecaptchaField mt={4} name="recaptcha-response" />
            <VStack spacing={4} mt={8}>
              <SpinnerButton
                id="form-confirm-edit-appointment-btn"
                btnTitle={<ChakraText>Potwierdź zmianę terminu</ChakraText>}
                w="250px"
              />
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
};

export { EditAppointmentForm };
